import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./style.css";
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Eye, Pencil, Trash2, X, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import EntityManager from './UserEntities';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { fmtDateVN } from './timeZone';
// Was a local `process.env.REACT_APP_BACKEND_URL || 'http://helinoxvn.com:5000'`, which made this
// screen call the PRODUCTION backend from the staging build (staging deliberately builds with an
// empty REACT_APP_BACKEND_URL and expects the same-origin fallback in backendConfig).
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import ModuleHelp from './ModuleHelp';
import { useTranslation } from 'react-i18next';

// A person's departments, main one first. A payload from before 2026-10-09 carries only the main
// department, so it reads as a list of one rather than as none.
const deptsOf = (u) => (Array.isArray(u?.departments)
    ? u.departments.map((d) => ({ teamKey: d.teamKey, isManager: !!d.isManager }))
    : (u?.sopTeamKey ? [{ teamKey: u.sopTeamKey, isManager: !!u.isTeamManager }] : []));

const rolesOf = (u) => String(u.Roles || u.UserRole || '')
  .split(',').map((r) => r.trim()).filter(Boolean);

/* ENTITY — Jay, 2026-10-08: "Can we make a group for the each users? HINC HUSA HEMA HEVN HPAC HEMY
   HAUS / MS / TS . etc". The catalogue is the server's (`/api/admin/entities`), so a new office or
   vendor is a row an Admin adds on this page and not a deploy. `NONE` is the group of people nobody
   has placed yet — every account on the day this went in. */
const NONE = '__none';
/* A partner company's own track (DAC VN, 2026-10-18) — staging only, with the rest of it (index.js). */
const PARTNER_ON = process.env.REACT_APP_PARTNER === '1';
const COLS = 12; // the checkbox, ten data columns and Actions — the group rows and the empty row span them all


/* THE GUIDE (Max 01/11: every module has one). The bulk bar only exists once rows are ticked, so the guide does not walk it. */
const TOUR = [
  { selector: '.lu-head-actions', title: 'tourUsers.actionsT', body: 'tourUsers.actionsB' },
  { selector: '.lu-filters', title: 'tourUsers.filtersT', body: 'tourUsers.filtersB' },
  { selector: '.lu-table thead', title: 'tourUsers.tableT', body: 'tourUsers.tableB' },
  { selector: '.lu-row .lu-actions', title: 'tourUsers.rowT', body: 'tourUsers.rowB' },
];

function ListUsers() {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [roleCatalog, setRoleCatalog] = useState([]);
    const [editing, setEditing] = useState(null); // { UserId, username, firstName, lastName, email, roles[], isActive }
    const [editErr, setEditErr] = useState('');
    const [saving, setSaving] = useState(false);

    // Esc = Cancel, except mid-save.
    useEscapeClose(() => setEditing(null), !!editing && !saving);
    const [msSearch, setMsSearch] = useState('');   // roles search filter
    const [teamOptions, setTeamOptions] = useState([]);
    const [entities, setEntities] = useState([]);
    /* GROUPED BY ENTITY unless somebody turns it off (Max, 2026-10-09: "mặc định group theo entity"),
       and every group starts SHUT (Max, 2026-10-27: "cho mặc định là collapse all nhé") — `opened` holds the ones
       somebody opened, so an entity added later arrives shut like the rest. A search or a column filter shows every
       group open, or what was found would sit inside a shut one. */
    const [groupBy, setGroupBy] = useState(true);
    /* TO ASSIGN (Jay task #1155): the active accounts nobody has placed yet — no entity, or no department */
    const [toAssign, setToAssign] = useState(false);
    const needsPlacing = (u) => !!u.IsActive && (!u.entityKey || !deptsOf(u).length);
    const [opened, setOpened] = useState(() => new Set());
    // Which Roles / Department cells are opened past their first chip, keyed `${UserId}:${column}`.
    const [openCells, setOpenCells] = useState(() => new Set());
    const [selected, setSelected] = useState(() => new Set());
    const [bulkEntity, setBulkEntity] = useState('');
    const [bulkMsg, setBulkMsg] = useState(null); // { ok, text }
    const [bulkBusy, setBulkBusy] = useState(false);
    const [entityPanel, setEntityPanel] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { fetchUsers(); }, []);

    const fetchEntities = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/admin/entities`, { withCredentials: true });
            setEntities(res.data.entities || []);
        } catch { setEntities([]); }
    };
    useEffect(() => { fetchEntities(); }, []);
    const entityOf = (key) => entities.find((e) => e.key === key);

    /* THE PARTNER COMPANIES (DAC VN …) for the "Partner company" picker — Max, 2026-10-18: a DAC account
       with no @dacpole.vn address is made a partner's by this flag. */
    const [partnerOrgs, setPartnerOrgs] = useState([]);
    useEffect(() => {
        if (!PARTNER_ON) return;
        axios.get(`${BACKEND_URL}/api/admin/external-orgs`, { withCredentials: true })
            .then((r) => setPartnerOrgs(r.data.orgs || [])).catch(() => setPartnerOrgs([]));
    }, []);
    const partnerOf = (key) => partnerOrgs.find((o) => o.orgKey === key);

    // Department list for the edit form. Silently absent if the caller has no tracker access.
    useEffect(() => {
        axios.get(`${BACKEND_URL}/api/development/project-tracker/teams`, { withCredentials: true })
            .then((res) => setTeamOptions(res.data.teams || []))
            .catch(() => setTeamOptions([]));
    }, []);

    // Role catalogue for the multi-select.
    useEffect(() => {
        axios.get(`${BACKEND_URL}/api/admin/role-options`)
            .then((res) => setRoleCatalog(res.data.roles || []))
            .catch(() => setRoleCatalog([]));
    }, []);

    const openEdit = async (user) => {
        setEditErr(''); setMsSearch('');
        let roles = [];
        let base = {
            username: user.UserName, firstName: user.UserFirstname, lastName: user.UserLastname,
            email: user.UserEmail, isActive: !!user.IsActive,
            departments: deptsOf(user),
            entityKey: user.entityKey || '',
            externalOrgKey: user.externalOrgKey || '',
            ceoDesks: [],
        };
        try {
            const res = await axios.get(`${BACKEND_URL}/user/${user.UserId}`);
            base = {
                username: res.data.UserName || '', firstName: res.data.UserFirstname || '',
                lastName: res.data.UserLastname || '', email: res.data.UserEmail || '',
                isActive: res.data.IsActive === 1,
                departments: deptsOf(res.data),
                entityKey: res.data.entityKey || '',
                externalOrgKey: res.data.externalOrgKey || '',
                ceoDesks: Array.isArray(res.data.ceoDesks) ? res.data.ceoDesks : [],
            };
            roles = Array.isArray(res.data.roles) && res.data.roles.length ? res.data.roles : (res.data.UserRole ? [res.data.UserRole] : []);
        } catch {
            roles = String(user.Roles || user.UserRole || '').split(',').map((r) => r.trim()).filter(Boolean);
        }
        setEditing({ UserId: user.UserId, ...base, roles });
    };

    /* SEVERAL DEPARTMENTS, A MANAGER TICK EACH — Max, 2026-10-09: "có trường hợp 1 user nằm ở 2
       department nên nhờ bạn chọn multichoice nhé". Kept in the catalogue's own order (Sales OP teams
       first), which is also how the server picks the MAIN department: the first row is the one a new
       quote is filed under and the one the team filter opens on, so the form marks it. */
    const sortDepts = (list) => [...list].sort((a, b) => {
        const ia = teamOptions.findIndex((t) => t.teamKey === a.teamKey);
        const ib = teamOptions.findIndex((t) => t.teamKey === b.teamKey);
        return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
    });
    const addDept = (key) => key && setEditing((prev) => (prev.departments.some((d) => d.teamKey === key) ? prev
        : { ...prev, departments: sortDepts([...prev.departments, { teamKey: key, isManager: false }]) }));
    const removeDept = (key) => setEditing((prev) => ({ ...prev, departments: prev.departments.filter((d) => d.teamKey !== key) }));
    const toggleDeptManager = (key) => setEditing((prev) => ({
        ...prev, departments: prev.departments.map((d) => (d.teamKey === key ? { ...d, isManager: !d.isManager } : d)),
    }));

    const toggleEditRole = (key) => setEditing((prev) => ({
        ...prev,
        roles: prev.roles.includes(key) ? prev.roles.filter((k) => k !== key) : [...prev.roles, key],
    }));

    const saveEdit = async () => {
        if (!editing) return;
        const e = editing;
        /* A partner's person may have no last name on file — DAC's accounts are made from the addresses Jay sends
           (Max, 2026-10-19, on Anna's form: "Username, first and last name are required"). */
        if (!e.username.trim() || !e.firstName.trim() || (!e.externalOrgKey && !e.lastName.trim())) {
            setEditErr(e.externalOrgKey ? t('xUsers.err.required2') : t('xUsers.err.required3')); return;
        }
        if (e.externalOrgKey && (e.departments || []).some((d) => d.isManager)) {
            setEditErr(t('xUsers.err.partnerManager')); return;
        }
        // A partner company's account may have no email; anybody else's must have a valid one.
        if ((e.email.trim() || !e.externalOrgKey) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.email.trim())) { setEditErr(t('xUsers.err.email')); return; }
        if (!e.roles.length) { setEditErr(t('xUsers.err.role')); return; }
        setSaving(true); setEditErr('');
        try {
            await axios.put(`${BACKEND_URL}/user/${e.UserId}`, {
                username: e.username.trim(), firstName: e.firstName.trim(), lastName: e.lastName.trim(),
                email: e.email.trim(), roles: e.roles, role: e.roles[0], isActive: e.isActive ? 1 : 0,
                departments: (e.departments || []).map((d) => ({ teamKey: d.teamKey, isManager: !!d.isManager })),
                entityKey: e.entityKey || '',
                // Sent only where the picker is drawn; the server keeps the flag when the field is absent.
                ...(PARTNER_ON ? { externalOrgKey: e.externalOrgKey || '' } : {}),
                ceoDesks: e.ceoDesks || [],
            }, { withCredentials: true });
            setEditing(null);
            fetchUsers(); fetchEntities();
        } catch (err) {
            setEditErr(err.response?.data?.error || t('xUsers.err.updateFailed'));
        } finally { setSaving(false); }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BACKEND_URL}/users`);
            setUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    /* PUT THE TICKED PEOPLE IN ONE ENTITY. The selection is cleared only when the save lands, so a
       refusal leaves the ticks where they were and the person can pick again. */
    const applyBulk = async () => {
        if (!selected.size) return;
        setBulkBusy(true); setBulkMsg(null);
        try {
            const res = await axios.put(`${BACKEND_URL}/api/admin/users/entity`,
                { userIds: [...selected], entityKey: bulkEntity === NONE ? '' : bulkEntity },
                { withCredentials: true });
            const where = bulkEntity === NONE ? t('xUsers.list.noEntity') : bulkEntity;
            setBulkMsg({ ok: true, text: res.data.unchanged
                ? t('xUsers.list.bulkMovedSome', { n: res.data.changed, where, m: res.data.unchanged })
                : t('xUsers.list.bulkMoved', { n: res.data.changed, where }) });
            setSelected(new Set()); setBulkEntity('');
            fetchUsers(); fetchEntities();
        } catch (err) {
            setBulkMsg({ ok: false, text: err.response?.data?.error || t('xUsers.list.bulkFailed') });
        } finally { setBulkBusy(false); }
    };
    const toggleSel = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const handleDelete = async (UserId, name) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(t('xUsers.list.confirmDelete', { name }))) return;
        try {
            await axios.delete(`${BACKEND_URL}/user/${UserId}`);
            fetchUsers();
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    const teamName = (k) => (teamOptions.find((t) => t.teamKey === k) || {}).teamName || k;
    /* ROLE NAMES, NOT KEYS (Jay task 02/11): the list printed "SOP Team", "Dev team", "Sale manager" while the picker says
       "S.OP Staff", "Developer", "S.OP Manager" — one role under two names. The keys stay what the code compares; what a
       person reads is the role's name from the catalogue (a key the catalogue does not know is shown as it is). */
    const roleLabel = (k) => roleCatalog.find((r) => r.roleKey === k)?.roleName || k;
    const rolesSorted = (u) => rolesOf(u).map(roleLabel).sort((a, b) => a.localeCompare(b));

    /* THE SEARCH BOX STAYS; THE THREE DROPDOWNS WENT — Max, 2026-10-09: "header cho tính năng sort và
       filter như excel", "bỏ các nút filter ở trên". Every column carries the portal's own Excel funnel
       (SortTh + useColumnTools, the pair Project Approval, GTM and Cash already use), so Roles, Active
       and Entity are filtered where they are read rather than in a second place above the table.
       A column sorts and filters on what its `get` returns, which is what the cell shows; Roles and
       Department return a LIST, so the funnel offers each role once instead of every combination. */
    const columns = [
        { key: 'UserId', get: (u) => Number(u.UserId) },
        { key: 'UserName', get: (u) => u.UserName || '' },
        { key: 'UserFirstname', get: (u) => u.UserFirstname || '' },
        { key: 'UserLastname', get: (u) => u.UserLastname || '' },
        { key: 'UserEmail', get: (u) => u.UserEmail || '' },
        { key: 'Roles', get: (u) => rolesSorted(u) },
        { key: 'Department', get: (u) => deptsOf(u).map((d) => teamName(d.teamKey)) },
        { key: 'entityKey', get: (u) => u.entityKey || '' },
        // The funnel lists what the cell prints, so the getter speaks the reader's language too.
        { key: 'IsActive', get: (u) => (u.IsActive ? t('xUsers.f.active') : t('xUsers.f.inactive')) },
        // An ISO day, so the funnel draws the Year > Month > Day tree (userCreatedAt is in FILTER_DATE_KEYS).
        { key: 'userCreatedAt', get: (u) => (u.UserCreatedAt ? fmtDateVN(u.UserCreatedAt, '') : '') },
    ];
    // The search narrows first; with no column sorted the list reads by ID, as it always has.
    const searched = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter((u) => !toAssign || needsPlacing(u)).filter((u) => !q || [u.UserName, u.UserFirstname, u.UserLastname, u.UserEmail, u.Roles || u.UserRole, rolesSorted(u).join(' '),
            u.entityKey, entityOf(u.entityKey)?.name]
            .map((x) => String(x || '').toLowerCase()).join(' ').includes(q))
            .sort((a, b) => Number(a.UserId) - Number(b.UserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, search, entities, roleCatalog, toAssign]);
    const tools = useColumnTools(searched, columns);
    const visibleUsers = tools.shown;

    /* GROUPED BY ENTITY, in the catalogue's own order (the Helinox companies, then the vendors), with
       the people nobody has placed yet LAST — they are the group somebody still has to work through,
       and putting them first would push every real group below the fold. Each group keeps whatever
       column is sorted, because grouping is a layout, not a different sort. */
    let groups = null;
    if (groupBy) {
        const byKey = new Map();
        visibleUsers.forEach((u) => {
            const k = u.entityKey || NONE;
            if (!byKey.has(k)) byKey.set(k, []);
            byKey.get(k).push(u);
        });
        const order = [...entities.map((e) => e.key), ...[...byKey.keys()].filter((k) => k !== NONE && !entityOf(k)), NONE];
        groups = order.filter((k) => byKey.has(k)).map((k) => ({ key: k, rows: byKey.get(k) }));
    }
    const finding = !!search || tools.active;
    const collapsed = { has: (k) => !finding && !opened.has(k) };
    const allShut = !!groups && groups.length > 0 && groups.every((g) => collapsed.has(g.key));
    const toggleGroup = (k) => setOpened((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k); else next.add(k);
        return next;
    });

    const visibleIds = visibleUsers.map((u) => u.UserId);
    const allTicked = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const filtersOn = !!search || tools.active;

    /* FOURTEEN ROWS, THEN THE TABLE SCROLLS — Max, 2026-10-09: "bảng chỉ hiện thị 10 dòng và có thể
       scroll down", then "à ko bảng chỉ hiện thì 14 dòng". MEASURED, not declared: a row's height is its
       padding plus whatever chip is in it, so the box is the header plus VISIBLE_ROWS of the shortest
       data rows on screen, worked out after each render. */
    const VISIBLE_ROWS = 14;
    const scrollRef = useRef(null);
    const [boxH, setBoxH] = useState(null);
    useLayoutEffect(() => {
        const box = scrollRef.current;
        if (!box) return;
        const head = box.querySelector('thead');
        const rows = [...box.querySelectorAll('tbody tr.lu-row')].slice(0, VISIBLE_ROWS + 2).map((r) => r.getBoundingClientRect().height);
        if (!head || !rows.length) return;
        const h = Math.ceil(head.getBoundingClientRect().height + VISIBLE_ROWS * Math.min(...rows)) + 1;
        setBoxH((prev) => (prev === h ? prev : h));
    }, [loading, groupBy, opened, finding, openCells, visibleUsers.length, teamOptions.length]);

    /* MANY ROLES, ONE LINE — Max, 2026-10-09: "trường hợp có nhiều role thì cần thể hiện sao cho gọn
       lại nhé". The first chip and a "+n" that opens the rest in place (and says them all on hover),
       so a person holding thirteen roles is one row high like everybody else — which is also what
       keeps the fourteen-row box honest. Per CELL, so opening one person's roles moves nobody else's. */
    const ChipCell = ({ id, col, items }) => {
        if (!items.length) return <span className="lu-muted">—</span>;
        const key = `${id}:${col}`;
        const open = openCells.has(key);
        const shown = open ? items : items.slice(0, 1);
        const more = items.length - 1;
        return (
            <div className={`lu-roles${open ? ' open' : ''}`}>
                {shown.map((r) => <span key={r} className="lu-role-chip" title={r}>{r}</span>)}
                {more > 0 && (
                    <button type="button" className="lu-more" aria-expanded={open}
                        title={open ? t('xUsers.list.showOne') : items.join(', ')}
                        onClick={() => setOpenCells((prev) => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                        })}>
                        {open ? '−' : `+${more}`}
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="lu-page">
            <Header />
            <div className="lu-shell">
                <div className="lu-card">
                    <div className="lu-head">
                        <h1>{t('xUsers.list.title')}</h1>
                        <div className="lu-head-actions">
                            <button onClick={() => navigate('/')} className="lu-btn ghost"><ArrowLeft size={16} /> {t('xUsers.list.back')}</button>
                            <button onClick={() => navigate('/admin/access-requests')} className="lu-btn ghost">{t('xUsers.list.accessRequests')}</button>
                            <button onClick={() => setEntityPanel(true)} className="lu-btn ghost" title={t('xUsers.list.entitiesTitle')}><Building2 size={16} /> {t('xUsers.list.entities')}</button>
                            <button onClick={() => navigate('/register')} className="lu-btn primary"><Plus size={16} /> {t('xUsers.list.createNew')}</button>
                            <ModuleHelp steps={TOUR} tourKey="listUsers" ready={!loading && users.length > 0} />
                        </div>
                    </div>

                    <div className="lu-filters">
                        <div className="lu-search">
                            <Search size={16} />
                            <input placeholder={t('xUsers.list.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <label className="lu-toggle"><input type="checkbox" checked={groupBy} onChange={(e) => setGroupBy(e.target.checked)} /> {t('xUsers.list.groupBy')}</label>
                        <label className="lu-toggle" title={t('xSignup.toAssignTitle')} data-lu-toassign>
                            <input type="checkbox" checked={toAssign} onChange={(e) => setToAssign(e.target.checked)} /> {t('xSignup.toAssign', { n: users.filter(needsPlacing).length })}
                        </label>
                        {groups && groups.length > 0 && (
                            <button type="button" className="lu-btn ghost small"
                                onClick={() => setOpened(allShut ? new Set(groups.map((g) => g.key)) : new Set())}>
                                {allShut ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {allShut ? t('xUsers.list.expandAll') : t('xUsers.list.collapseAll')}
                            </button>
                        )}
                        {filtersOn && (
                            <button type="button" className="lu-clear" onClick={() => { setSearch(''); tools.clear(); }}>
                                <X size={14} /> {t('xUsers.list.clearFilters')}
                            </button>
                        )}
                        {/* ACTIVE ACCOUNTS ONLY in the numbers (Jay, 30/10: "Inactive accounts are included in that number, so it
                            does not show how many people actually use the Portal"). The inactive rows stay in the list; how many of
                            them are showing is said beside the count, so a filter on Inactive does not read as an empty table. */}
                        <span className="lu-count">{t('xUsers.list.count', { shown: visibleUsers.filter((u) => u.IsActive).length, total: users.filter((u) => u.IsActive).length })}
                            {visibleUsers.some((u) => !u.IsActive) && t('xUsers.list.countInactive', { n: visibleUsers.filter((u) => !u.IsActive).length })}</span>
                    </div>

                    {(selected.size > 0 || bulkMsg) && (
                        <div className="lu-bulk">
                            {selected.size > 0 && (
                                <>
                                    <b>{t('xUsers.list.selected', { n: selected.size })}</b>
                                    <span>{t('xUsers.list.setEntity')}</span>
                                    <select value={bulkEntity} onChange={(e) => setBulkEntity(e.target.value)} aria-label={t('xUsers.list.bulkAria')}>
                                        <option value="">{t('xUsers.list.pick')}</option>
                                        {['helinox', 'partner', 'vendor'].map((kind) => (
                                            <optgroup key={kind} label={kind === 'helinox' ? 'Helinox' : t(kind === 'partner' ? 'xUsers.ent.kindPartner' : 'xUsers.ent.kindVendor')}>
                                                {entities.filter((en) => en.kind === kind && en.isActive).map((en) => (
                                                    <option key={en.key} value={en.key}>{en.key}{en.name !== en.key ? ` — ${en.name}` : ''}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                        <option value={NONE}>{t('xUsers.list.noEntityClear')}</option>
                                    </select>
                                    <button type="button" className="lu-btn primary small" disabled={!bulkEntity || bulkBusy} onClick={applyBulk}>{bulkBusy ? t('xUsers.list.saving') : t('xUsers.list.apply')}</button>
                                    <button type="button" className="lu-btn ghost small" onClick={() => setSelected(new Set())}>{t('xUsers.list.clearSelection')}</button>
                                </>
                            )}
                            {bulkMsg && <span className={`lu-bulk-msg ${bulkMsg.ok ? 'ok' : 'err'}`}>{bulkMsg.text}</span>}
                        </div>
                    )}

                    {loading ? <p className="lu-empty">{t('xUsers.list.loading')}</p> : (
                        <div className="lu-table-scroll" ref={scrollRef} style={boxH ? { maxHeight: boxH } : undefined}>
                            <table className="lu-table">
                                <thead>
                                    <tr>
                                        <th className="lu-sel" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" aria-label={t('xUsers.list.selectAll')} checked={allTicked}
                                                onChange={() => setSelected((prev) => {
                                                    const next = new Set(prev);
                                                    if (allTicked) visibleIds.forEach((id) => next.delete(id)); else visibleIds.forEach((id) => next.add(id));
                                                    return next;
                                                })} />
                                        </th>
                                        <SortTh label={t('xUsers.f.id')} {...tools.thProps('UserId')} />
                                        <SortTh label={t('xUsers.f.username')} {...tools.thProps('UserName')} />
                                        <SortTh label={t('xUsers.f.firstNameCap')} className="hide-mobile" {...tools.thProps('UserFirstname')} />
                                        <SortTh label={t('xUsers.f.lastNameCap')} className="hide-mobile" {...tools.thProps('UserLastname')} />
                                        <SortTh label={t('xUsers.f.email')} {...tools.thProps('UserEmail')} />
                                        <SortTh label={t('xUsers.f.roles')} {...tools.thProps('Roles')} />
                                        {/* Department: the project tracker scopes by it, ticket codes route by
                                            it, and it decides who may edit what — so it lives on the account. */}
                                        <SortTh label={t('xUsers.f.department')} className="hide-mobile" {...tools.thProps('Department')} />
                                        <SortTh label={t('xUsers.f.entity')} {...tools.thProps('entityKey')} />
                                        <SortTh label={t('xUsers.f.active')} className="hide-mobile" {...tools.thProps('IsActive')} />
                                        <SortTh label={t('xUsers.f.created')} className="hide-mobile" {...tools.thProps('userCreatedAt')} />
                                        <th>{t('xUsers.list.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(groups || [{ key: null, rows: visibleUsers }]).map((g) => (
                                      <React.Fragment key={g.key || 'all'}>
                                        {g.key && (() => {
                                            const en = entityOf(g.key);
                                            const ids = g.rows.map((u) => u.UserId);
                                            const all = ids.every((id) => selected.has(id));
                                            return (
                                                <tr className="lu-group">
                                                    <td className="lu-sel">
                                                        <input type="checkbox" aria-label={t('xUsers.list.selectGroup', { name: g.key === NONE ? t('xUsers.list.noEntity') : g.key })} checked={all}
                                                            onChange={() => setSelected((prev) => {
                                                                const next = new Set(prev);
                                                                ids.forEach((id) => (all ? next.delete(id) : next.add(id)));
                                                                return next;
                                                            })} />
                                                    </td>
                                                    <td colSpan={COLS - 1}>
                                                        <button type="button" className="lu-fold" aria-expanded={!collapsed.has(g.key)}
                                                            onClick={() => toggleGroup(g.key)}
                                                            title={collapsed.has(g.key) ? t('xUsers.list.expandGroup') : t('xUsers.list.collapseGroup')}
                                                            aria-label={t(collapsed.has(g.key) ? 'xUsers.list.expandNamed' : 'xUsers.list.collapseNamed', { name: g.key === NONE ? t('xUsers.list.noEntity') : g.key })}>
                                                            {collapsed.has(g.key) ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                                                        </button>
                                                        {g.key === NONE
                                                            ? <b>{t('xUsers.list.noEntityYet')}</b>
                                                            : <><b>{g.key}</b>{en && en.name !== g.key && <span className="lu-group-name">{en.name}</span>}
                                                                {en?.kind === 'vendor' && <span className="lu-ent vendor">{t('xUsers.ent.kindVendor')}</span>}
                                                                {en?.kind === 'partner' && <span className="lu-ent partner">{t('xUsers.ent.kindPartner')}</span>}
                                                                {en && !en.isActive && <span className="lu-muted"> {t('xUsers.list.notInUse')}</span>}</>}
                                                        {/* the entity's headcount = its active accounts (Jay, 30/10); its inactive rows are still listed under it */}
                                                        {(() => { const n = g.rows.filter((u) => u.IsActive).length; return <span className="lu-group-n">{t(n === 1 ? 'xUsers.list.groupPerson' : 'xUsers.list.groupPeople', { n })}</span>; })()}
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                    {(g.key && collapsed.has(g.key) ? [] : g.rows).map((user) => (
                                        <tr key={user.UserId} className={`lu-row${selected.has(user.UserId) ? ' is-sel' : ''}`}>
                                            <td className="lu-sel"><input type="checkbox" aria-label={t('xUsers.list.selectUser', { name: user.UserName })} checked={selected.has(user.UserId)} onChange={() => toggleSel(user.UserId)} /></td>
                                            <td className="mono">{user.UserId}</td>
                                            <td className="strong">
                                                {user.UserName}
                                                {user.externalOrgKey && (
                                                    <span className="lu-ent partner" title={t('xUsers.list.partnerTitle', { org: partnerOf(user.externalOrgKey)?.orgName || user.externalOrgKey })}>
                                                        {t('xUsers.list.partnerChip', { org: user.externalOrgKey })}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="hide-mobile">{user.UserFirstname}</td>
                                            <td className="hide-mobile">{user.UserLastname}</td>
                                            <td>{user.UserEmail}</td>
                                            <td><ChipCell id={user.UserId} col="roles" items={rolesSorted(user)} /></td>
                                            <td className="hide-mobile">
                                                <ChipCell id={user.UserId} col="dept"
                                                    items={deptsOf(user).map((d) => (d.isManager ? t('xUsers.list.deptManager', { team: teamName(d.teamKey) }) : teamName(d.teamKey)))} />
                                            </td>
                                            <td>
                                                {user.entityKey
                                                    ? <span className={`lu-ent ${entityOf(user.entityKey)?.kind || ''}`} title={entityOf(user.entityKey)?.name || user.entityKey}>{user.entityKey}</span>
                                                    : <span className="lu-muted">—</span>}
                                            </td>
                                            <td className="hide-mobile">
                                                <span className={`lu-pill ${user.IsActive ? 'on' : 'off'}`}>{user.IsActive ? t('xUsers.f.active') : t('xUsers.f.inactive')}</span>
                                            </td>
                                            <td className="hide-mobile lu-muted lu-nowrap">{fmtDateVN(user.UserCreatedAt)}</td>
                                            <td>
                                                <div className="lu-actions">
                                                    <button onClick={() => navigate(`/profile/${user.UserId}`)} title={t('xUsers.list.viewProfile')}><Eye size={15} /></button>
                                                    <button onClick={() => openEdit(user)} title={t('xUsers.list.edit')}><Pencil size={15} /></button>
                                                    <button className="danger" onClick={() => handleDelete(user.UserId, user.UserName)} title={t('xUsers.list.delete')}><Trash2 size={15} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                      </React.Fragment>
                                    ))}
                                    {!visibleUsers.length && <tr><td colSpan={COLS} className="lu-empty">{t('xUsers.list.noMatch')}</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {editing && (
                <div className="eu-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
                    <div className="eu-modal">
                        <div className="eu-modal-head"><h3>{t('xUsers.edit.title')}</h3><button type="button" onClick={() => setEditing(null)}>&times;</button></div>
                        <div className="eu-modal-body eu-two-col">
                            <div className="eu-col-left">
                                <label>{t('xUsers.f.username')}<input value={editing.username} onChange={(e) => setEditing({ ...editing, username: e.target.value })} /></label>
                                <label>{editing.externalOrgKey ? t('xUsers.f.emailOptional') : t('xUsers.f.email')}<input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
                                <label>{t('xUsers.f.firstName')}<input value={editing.firstName} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} /></label>
                                <label>{editing.externalOrgKey ? t('xUsers.f.lastNameOptional') : t('xUsers.f.lastName')}<input value={editing.lastName} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} /></label>
                                {/* WHICH ENTITY — the company (or vendor) this person works for; what the
                                    user list groups by. An entity switched off since this person was put in
                                    it stays on the list for THEM, marked, so opening the form does not
                                    quietly move them out of it. */}
                                <label>{t('xUsers.f.entity')}
                                    <select value={editing.entityKey || ''} onChange={(e) => setEditing({ ...editing, entityKey: e.target.value })}>
                                        <option value="">{t('xUsers.edit.none')}</option>
                                        {['helinox', 'partner', 'vendor'].map((kind) => (
                                            <optgroup key={kind} label={kind === 'helinox' ? 'Helinox' : t(kind === 'partner' ? 'xUsers.ent.kindPartner' : 'xUsers.ent.kindVendor')}>
                                                {entities.filter((en) => en.kind === kind && (en.isActive || en.key === editing.entityKey)).map((en) => (
                                                    <option key={en.key} value={en.key}>{en.key}{en.name !== en.key ? ` — ${en.name}` : ''}{en.isActive ? '' : ` ${t('xUsers.edit.notInUseOpt')}`}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </label>
                                {/* A PARTNER COMPANY'S ACCOUNT (DAC VN …) — Max, 2026-10-18. An @dacpole.vn address is
                                    DAC's whatever is picked here (the server says so if they disagree); the
                                    flag is for a DAC account with no such address. Such an account opens its
                                    company's sample track and nothing else. */}
                                {PARTNER_ON && (
                                    <label>{t('xUsers.f.partnerCompany')}
                                        <select value={editing.externalOrgKey || ''} onChange={(e) => setEditing({ ...editing, externalOrgKey: e.target.value })}>
                                            <option value="">{t('xUsers.f.noPartner')}</option>
                                            {partnerOrgs.filter((o) => o.active || o.orgKey === editing.externalOrgKey).map((o) => (
                                                <option key={o.orgKey} value={o.orgKey}>{o.orgName}{o.active ? '' : ` ${t('xUsers.edit.switchedOff')}`}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                {/* Sales OP team: what the Project Tracker and Ticket Station scope by.
                                    Members are added here over time, as Jay asked — not all on day one. */}
                                <div className="eu-fieldset">
                                    <span className="eu-fieldset-title">{t('xUsers.f.department')}</span>
                                    {/* One row per department, the Manager tick on the ROW: managing one
                                        team says nothing about the other. What a department covers comes
                                        from Jay's email and is stored on the team record, so it is shown
                                        under its own row — whoever assigns can see it is the right one. */}
                                    {editing.departments.map((d, i) => {
                                        const team = teamOptions.find((t) => t.teamKey === d.teamKey);
                                        return (
                                            <div key={d.teamKey} className="eu-dept">
                                                <div className="eu-dept-row">
                                                    <span className="eu-dept-name">
                                                        {team?.teamName || d.teamKey}
                                                        {i === 0 && editing.departments.length > 1 && (
                                                            <span className="eu-dept-main" title={t('xUsers.edit.mainTitle')}>{t('xUsers.edit.main')}</span>
                                                        )}
                                                    </span>
                                                    {/* A partner's person is in a department to say what they do on their own
                                                        track; managing an HEVN department is not theirs to hold (the server
                                                        refuses it too). */}
                                                    <label className={`eu-check eu-check-boxed${editing.externalOrgKey ? ' is-off' : ''}`}
                                                        title={editing.externalOrgKey ? t('xUsers.err.partnerManager') : undefined}>
                                                        <input type="checkbox" checked={!!d.isManager} disabled={!!editing.externalOrgKey && !d.isManager}
                                                            onChange={() => toggleDeptManager(d.teamKey)} /> {t('xUsers.edit.manager')}
                                                    </label>
                                                    <button type="button" className="eu-dept-x" onClick={() => removeDept(d.teamKey)}
                                                        title={t('xUsers.edit.remove', { name: team?.teamName || d.teamKey })} aria-label={t('xUsers.edit.remove', { name: team?.teamName || d.teamKey })}><X size={15} /></button>
                                                </div>
                                                {team?.description && <div className="eu-hint eu-dept-hint" title={team.description}>{team.description}</div>}
                                            </div>
                                        );
                                    })}
                                    {/* Sales OP teams first, then the receiving functions — the API already
                                        returns them in that order. Only the ones not on the person yet. */}
                                    <select className="eu-dept-add" value="" onChange={(e) => addDept(e.target.value)}
                                        aria-label={t('xUsers.edit.addDeptAria')}>
                                        <option value="">{editing.departments.length ? t('xUsers.edit.addAnother') : t('xUsers.edit.addFirst')}</option>
                                        {teamOptions.filter((t) => !editing.departments.some((d) => d.teamKey === t.teamKey))
                                            .map((t) => <option key={t.teamKey} value={t.teamKey}>{t.teamName}</option>)}
                                    </select>
                                    {!editing.departments.length && <div className="eu-hint">{t('xUsers.edit.noDept')}</div>}
                                </div>

                                {/* WHICH CEO OFFICE DESK — Max, 2026-09-23. It went on the standalone
                                    /edit-user page first, which is the wrong screen: the editor
                                    people actually open is this modal, from the user list.
                                    Its own fieldset rather than a row inside Department, because a
                                    department is where somebody works and a desk is what they may
                                    SEE — Young's rule 2 is access, not a filter. Two ticks and not a
                                    dropdown: a person can be on both (Young is) and on neither,
                                    which is the safe default. */}
                                <div className="eu-fieldset">
                                    <span className="eu-fieldset-title">{t('xUsers.f.ceoDesk')}</span>
                                    <div className="eu-row2">
                                        {[['business', 'Business'], ['executive', 'Executive']].map(([key, label]) => (
                                            <label key={key} className="eu-check eu-check-boxed">
                                                <input
                                                    type="checkbox"
                                                    checked={(editing.ceoDesks || []).includes(key)}
                                                    onChange={(ev) => setEditing((prev) => ({
                                                        ...prev,
                                                        ceoDesks: ev.target.checked
                                                            ? [...(prev.ceoDesks || []), key]
                                                            : (prev.ceoDesks || []).filter((d) => d !== key),
                                                    }))}
                                                /> {label}
                                            </label>
                                        ))}
                                    </div>
                                    <div className="eu-hint">
                                        {(editing.ceoDesks || []).length
                                            ? t('xUsers.edit.deskHint')
                                            : t('xUsers.edit.deskNone')}
                                    </div>
                                </div>
                                <label className="eu-check"><input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} /> {t('xUsers.f.active')}</label>
                                {editErr && <div className="eu-error">{editErr}</div>}
                            </div>
                            <div className="eu-col-right">
                                {(() => {
                                    const keys = roleCatalog.map((o) => o.roleKey);
                                    const extras = editing.roles.filter((k) => !keys.includes(k)).map((k) => ({ roleKey: k, roleName: t('xUsers.f.legacyRole', { name: k }) }));
                                    const all = [...roleCatalog, ...extras];
                                    const q = msSearch.trim().toLowerCase();
                                    const filtered = q ? all.filter((o) => o.roleName.toLowerCase().includes(q)) : all;
                                    return (
                                        <>
                                            <span className="eu-label">{t('xUsers.f.roles')} <span className="eu-role-count">{t('xUsers.f.rolesSelected', { n: editing.roles.length })}</span></span>
                                            <input className="ms-search" placeholder={t('xUsers.f.searchRoles')} value={msSearch} onChange={(e) => setMsSearch(e.target.value)} />
                                            <div className="eu-roles-list">
                                                {filtered.map((o) => (
                                                    <label key={o.roleKey} className={`ms-row${editing.roles.includes(o.roleKey) ? ' on' : ''}`}>
                                                        <input type="checkbox" checked={editing.roles.includes(o.roleKey)} onChange={() => toggleEditRole(o.roleKey)} />
                                                        {o.roleName}
                                                    </label>
                                                ))}
                                                {!filtered.length && <div className="ms-empty">{t('xUsers.f.noRoles')}</div>}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        <div className="eu-modal-foot">
                            <button type="button" className="lu-btn primary" disabled={saving} onClick={saveEdit}>{saving ? t('xUsers.edit.saving') : t('xUsers.edit.saveChanges')}</button>
                            <button type="button" className="lu-btn ghost" onClick={() => setEditing(null)}>{t('xUsers.edit.cancel')}</button>
                        </div>
                    </div>
                </div>
            )}

            {entityPanel && <EntityManager entities={entities} onClose={() => setEntityPanel(false)} onChanged={() => { fetchEntities(); fetchUsers(); }} />}

            <style jsx="true">{`
                .lu-page { background: #F4F6F9; min-height: 100vh; }
                .lu-shell { margin-top: 64px; padding: 24px; transition: margin-left 180ms ease, width 180ms ease, max-width 180ms ease; }
                /* Shift into the remaining space when the sidebar is open so the card re-centers. */
                @media (min-width: 900px) {
                    body.portal-shell.portal-sidebar-open .lu-shell {
                        margin-left: 270px;
                        width: calc(100% - 270px);
                        max-width: calc(100% - 270px);
                    }
                }
                .lu-card {
                    background: #fff; border: 1px solid #D8E0EC; border-radius: 14px;
                    /* 1360 and not 1200: the Entity column and the tick column (Jay, 2026-10-08) took
                       the table past the old cap, which had already been 24px too narrow for it, so
                       Actions sat off the right edge behind a sideways scroll. Measured, not guessed. */
                    max-width: 1440px; margin: 0 auto; padding: 24px 28px;
                    box-shadow: 0 1px 4px rgba(31,56,100,.06);
                }
                .lu-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
                .lu-head h1 { font-size: 22px; font-weight: 900; color: #1F3864; margin: 0; }
                .lu-head-actions { display: flex; gap: 10px; }
                .lu-btn { display: inline-flex; align-items: center; gap: 7px; border-radius: 8px; padding: 9px 16px; font-size: 14px; font-weight: 700; cursor: pointer; border: 1px solid #D8E0EC; background: #fff; color: #1F3864; }
                .lu-btn.primary { background: #1F3864; color: #fff; border-color: #1F3864; }
                .lu-btn.primary svg { stroke: #fff; }
                .lu-btn:hover { filter: brightness(0.97); }

                .lu-filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 16px; }
                .lu-search { display: flex; align-items: center; gap: 8px; border: 1px solid #D8E0EC; border-radius: 9px; padding: 8px 12px; flex: 1 1 300px; min-width: 220px; color: #6B7280; }
                .lu-search input { border: none; outline: none; font-size: 14px; width: 100%; background: transparent; color: #111827; }
                .lu-filters select { border: 1px solid #D8E0EC; border-radius: 9px; padding: 9px 12px; font-size: 14px; background: #fff; color: #1F3864; font-weight: 600; cursor: pointer; }
                .lu-clear { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #F5C2C2; background: #FDECEC; color: #B42318; border-radius: 9px; padding: 8px 12px; font-size: 13px; font-weight: 700; cursor: pointer; }
                .lu-clear:hover { background: #FBDCDC; }
                .lu-count { font-size: 13px; color: #6B7280; font-weight: 600; margin-left: auto; }
                .lu-toggle { display: inline-flex; align-items: center; gap: 7px; font-size: 13.5px; font-weight: 700; color: #1F3864; cursor: pointer; border: 1px solid #D8E0EC; border-radius: 9px; padding: 8px 12px; background: #fff; }
                .lu-toggle input { width: 15px; height: 15px; }
                .lu-btn.ghost svg, .lu-btn:not(.primary) svg * { color: inherit; }
                .lu-btn.small { padding: 6px 12px; font-size: 13px; }
                .lu-btn:disabled { opacity: .5; cursor: default; }
                .lu-btn.primary svg * { stroke: #fff; }

                /* The bulk bar sits between the filters and the table, where the ticks are. */
                .lu-bulk { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: -4px 0 14px; padding: 9px 12px; border: 1px solid #BFD3EE; background: #F1F6FD; border-radius: 10px; font-size: 13.5px; color: #1F3864; }
                .lu-bulk select { border: 1px solid #D8E0EC; border-radius: 8px; padding: 6px 10px; font-size: 13.5px; background: #fff; color: #1F3864; font-weight: 600; }
                .lu-bulk-msg { font-weight: 700; }
                .lu-bulk-msg.ok { color: #15803D; }
                .lu-bulk-msg.err { color: #B42318; }

                .lu-table th.lu-sel, .lu-table td.lu-sel { width: 30px; padding-right: 0; cursor: default; }
                .lu-sel input { width: 15px; height: 15px; cursor: pointer; }
                .lu-table tbody tr.is-sel td { background: #F3F7FD; }
                .lu-table tr.lu-group td { background: #EAF0F8; color: #1F3864; font-size: 13px; border-bottom: 1px solid #D8E0EC; padding-top: 8px; padding-bottom: 8px; }
                .lu-table tr.lu-group:hover td { background: #EAF0F8; }
                .lu-group-name { margin-left: 8px; color: #475569; font-weight: 600; }
                .lu-group-n { margin-left: 12px; color: #6B7280; font-weight: 600; font-size: 12px; }
                /* Vendor reads differently from a Helinox company at a glance, and the word is printed
                   in the group header so the colour is never the only thing saying it. */
                .lu-ent { font-size: 11.5px; font-weight: 800; background: #E6EEF9; color: #1F3864; padding: 2px 9px; border-radius: 20px; white-space: nowrap; }
                .lu-ent.vendor { background: #FDF0DC; color: #8A4B08; margin-left: 8px; }
                .lu-ent.partner { background: #FCE7F3; color: #9D174D; margin-left: 8px; font-size: 11px; }
                td > .lu-ent.vendor, td > .lu-ent.partner { margin-left: 0; }

                /* The box scrolls both ways and the header stays put while it does (the height is set from
                   the rows themselves, see boxH). */
                .lu-table-scroll { overflow: auto; border-radius: 8px; }
                .lu-table thead th { position: sticky; top: 0; z-index: 3; }
                .lu-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .lu-table th, .lu-table td { padding: 11px 8px; text-align: left; border-bottom: 1px solid #EEF2F7; font-size: 13.5px; }
                .lu-table th { background: #F1F5F9; color: #334155; font-weight: 800; cursor: pointer; white-space: nowrap; user-select: none; font-size: 12.5px; }
                .lu-table th:first-child { border-top-left-radius: 8px; }
                .lu-table th:last-child { border-top-right-radius: 8px; cursor: default; }
                .lu-table tbody tr:hover td { background: #FAFBFE; }
                .lu-table td.mono { font-family: ui-monospace, monospace; color: #6B7280; }
                .lu-table td.strong { font-weight: 700; color: #1F3864; }
                .lu-muted { color: #9CA3AF; }

                .lu-roles { display: flex; flex-wrap: nowrap; gap: 5px; align-items: center; }
                .lu-roles.open { flex-wrap: wrap; }
                /* A long department name is cut, not wrapped: the row stays one line high, and the chip
                   says the whole name on hover. */
                .lu-roles .lu-role-chip { white-space: nowrap; max-width: 150px; overflow: hidden; text-overflow: ellipsis; }
                .lu-roles.open .lu-role-chip { max-width: none; }
                .lu-table td.lu-nowrap { white-space: nowrap; }
                .lu-more { border: 1px solid #C9D6EA; background: #fff; color: #1F3864; border-radius: 20px; font-size: 11px;
                    font-weight: 800; padding: 1px 7px; cursor: pointer; line-height: 1.5; white-space: nowrap; }
                .lu-more:hover { background: #EAF0F8; }
                .lu-fold { border: none; background: transparent; padding: 0 6px 0 0; cursor: pointer; vertical-align: middle;
                    color: #1F3864; display: inline-flex; align-items: center; }
                .lu-fold svg, .lu-fold svg * { color: inherit; }
                /* The shared Excel header (SortTh), on this table's light head rather than a navy one. */
                .lu-table th.sortable-th { padding: 0; }
                .lu-table th.sortable-th .th-label { display: inline-block; padding: 11px 2px 11px 8px; cursor: pointer; }
                .lu-table th.sortable-th .th-funnel { border: none; background: transparent; cursor: pointer; padding: 2px 4px;
                    margin-right: 6px; border-radius: 4px; vertical-align: middle; color: #94A3B8; display: inline-flex; }
                .lu-table th.sortable-th .th-funnel:hover { background: #E2E8F0; }
                .lu-table th.sortable-th .th-funnel svg, .lu-table th.sortable-th .th-funnel svg * { color: inherit; stroke: currentColor; }
                .lu-table th.sortable-th.active .th-funnel, .lu-table th.sortable-th.filtered .th-funnel { color: #2E75B6; }
                .lu-table th.sortable-th.filtered { background: #E3ECF8; }
                .lu-role-chip { font-size: 11.5px; font-weight: 700; background: #EAF0F8; color: #1F3864; padding: 2px 9px; border-radius: 20px; }
                .lu-pill { font-size: 11.5px; font-weight: 700; padding: 2px 10px; border-radius: 20px; }
                .lu-pill.on { background: #E4F6EC; color: #15803D; }
                .lu-pill.off { background: #FDECEC; color: #B42318; }

                .lu-actions { display: flex; gap: 4px; }
                .lu-actions button { border: 1px solid #E5EAF2; background: #fff; border-radius: 7px; padding: 5px 7px; cursor: pointer; color: #1F3864; display: inline-flex; }
                .lu-actions button:hover { background: #F1F5F9; }
                .lu-actions button.danger { color: #DC2626; }
                .lu-empty { text-align: center; color: #9CA3AF; padding: 28px; font-size: 14px; }

                .hide-mobile { display: table-cell; }
                @media (max-width: 820px) { .hide-mobile { display: none; } }

                /* Edit modal */
                .eu-overlay { position: fixed; inset: 0; background: rgba(17,24,39,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
                /* Flex column + a scrolling body: the modal grew past the viewport once the Sales OP
                   fields were added, and overflow:hidden was silently clipping the last rows. */
                .eu-modal { background: #fff; border-radius: 14px; width: 100%; max-width: 760px; box-shadow: 0 24px 60px rgba(0,0,0,.28); overflow: hidden; display: flex; flex-direction: column; max-height: calc(100vh - 40px); }
                .eu-modal-head, .eu-modal-foot { flex: none; }
                .eu-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #EEF2F7; }
                .eu-modal-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #1F3864; }
                .eu-modal-head button { border: none; background: none; font-size: 24px; line-height: 1; color: #9CA3AF; cursor: pointer; }
                .eu-modal-body { padding: 20px; overflow-y: auto; }
                /* minmax(0, …): a bare 1fr will not go below its content's own width, so a long department
                   description (Sales OP Team 1's) widened the column and pushed the form out sideways. */
                .eu-two-col { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 22px; }
                @media (max-width: 680px) { .eu-two-col { grid-template-columns: 1fr; } }
                .eu-col-left { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
                .eu-modal-body label { display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; font-weight: 700; color: #374151; }
                .eu-col-right { display: flex; flex-direction: column; }
                .eu-role-count { font-weight: 600; color: #6B7280; font-size: 11.5px; }
                .eu-roles-list { margin-top: 6px; border: 1px solid #E5EAF2; border-radius: 8px; padding: 4px; max-height: 260px; overflow-y: auto; flex: 1; }
                .eu-modal-body input[type="text"], .eu-modal-body input[type="email"], .eu-modal-body input:not([type]) {
                    border: 1px solid #D8E0EC; border-radius: 8px; padding: 9px 11px; font-size: 14px; font-weight: 500; color: #111827;
                }
                /* Selects were unstyled, so the Sales OP team dropdown fell back to the browser
                   default and did not line up with the inputs next to it. */
                .eu-modal-body select {
                    border: 1px solid #D8E0EC; border-radius: 8px; padding: 9px 11px; font-size: 14px; font-weight: 500;
                    color: #111827; background: #fff; height: 38px; width: 100%; box-sizing: border-box; cursor: pointer;
                }
                .eu-modal-body input:focus, .eu-modal-body select:focus { outline: none; border-color: #2E75B6; }
                .eu-check { flex-direction: row !important; align-items: center; gap: 8px; font-size: 14px; }
                .eu-check input { width: 16px; height: 16px; }

                .eu-label { display: block; font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 8px; }

                /* Roles side panel */
                .ms-search { width: 100%; box-sizing: border-box; border: 1px solid #E5EAF2; border-radius: 7px; padding: 8px 10px; font-size: 13px; }
                .ms-search:focus { outline: none; border-color: #2E75B6; }
                .ms-row { display: flex !important; flex-direction: row !important; align-items: center; gap: 9px; font-size: 13.5px !important; font-weight: 600 !important; color: #334155 !important; padding: 8px 9px; border-radius: 6px; cursor: pointer; }
                .ms-row:hover { background: #F3F6FB; }
                .ms-row.on { background: #EAF0F8; color: #1F3864 !important; }
                .ms-row input { width: 15px; height: 15px; }
                .ms-empty { padding: 10px; font-size: 13px; color: #9CA3AF; }

                /* Sales OP block — a boxed group so the two extra fields don't read as loose
                   strays among the account fields. */
                .eu-fieldset { border: 1px solid #E5EAF2; border-radius: 10px; padding: 12px 13px 11px; background: #F9FBFD; }
                .eu-fieldset-title { display: block; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #6B7280; margin-bottom: 9px; }
                .eu-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; align-items: end; }
                @media (max-width: 560px) { .eu-row2 { grid-template-columns: 1fr; align-items: stretch; } }
                .eu-check-boxed { border: 1px solid #D8E0EC; border-radius: 8px; background: #fff; padding: 9px 11px; height: 38px; box-sizing: border-box; }
                .eu-check-boxed.is-off { opacity: .55; }
                .eu-hint { margin-top: 9px; font-size: 11.5px; line-height: 1.45; color: #6B7280; }
                .eu-dept { margin-bottom: 10px; }
                .eu-dept-row { display: grid; grid-template-columns: 1fr auto 32px; gap: 8px; align-items: center; }
                .eu-dept-name { display: flex; align-items: center; gap: 7px; min-width: 0; height: 38px; box-sizing: border-box;
                    border: 1px solid #D8E0EC; border-radius: 8px; background: #fff; padding: 0 11px; font-size: 14px; font-weight: 600; color: #111827; }
                .eu-dept-main { font-size: 10.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #1F3864;
                    background: #EAF0F8; border-radius: 4px; padding: 2px 6px; }
                .eu-dept-x { height: 32px; width: 32px; border: 1px solid #E5EAF2; border-radius: 7px; background: #fff; color: #9CA3AF;
                    display: inline-flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
                .eu-dept-x:hover { color: #B42318; border-color: #F5C2C2; }
                /* The global "* { color }" rule reaches the icon's own shapes, so they are told to inherit. */
                .eu-dept-x svg, .eu-dept-x svg * { color: inherit; }
                .eu-dept-hint { margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .eu-error { margin-top: 14px; font-size: 13px; font-weight: 600; color: #B42318; background: #FDECEC; border: 1px solid #F5C2C2; border-radius: 8px; padding: 9px 12px; }
                .eu-modal-foot { display: flex; gap: 10px; padding: 16px 20px; border-top: 1px solid #EEF2F7; }
            `}</style>
        </div>
    );
}

export default ListUsers;
